import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import PosAPI from "../../api/inventory/posApi";
import categoryAPI from "../../api/categoryApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { computeBill, inr } from "../../utils/posBilling";
import PosProductSearch from "../../components/PosProductSearch";
import PosCustomerSearch, { EMPTY_CUSTOMER } from "../../components/PosCustomerSearch";
import PaymentModal from "../../components/PaymentModal";
import "./pos.css";
import CartPrice from "../../components/CartPrice";
import PosCouponBox from "../../components/PosCouponBox";

const CART_KEY = "pos_cart_v2";
const newRef = () => window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const loadSaved = () => {
  try {
    return JSON.parse(sessionStorage.getItem(CART_KEY)) || null;
  } catch {
    return null;
  }
};

const gridToRow = (p, v) => ({
  productId: p.id,
  variantId: v.id,
  title: p.title,
  variantName: v.name,
  sku: v.sku,
  price: v.price,
  mrp: v.mrp,
  stock: v.stock,
  gst: p.gst,
  unit: p.unit,
  thumbnail: v.image || p.thumbnail,
});

const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;

function List() {
  const navigate = useNavigate();
  const saved = loadSaved();
 
  /* ---------------- PRODUCT GRID ---------------- */
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState("");
  const [products, setProducts] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
 
  /* ---------------- SALE ---------------- */
  const [cart, setCart] = useState(saved?.cart || []);
  const [customer, setCustomer] = useState(saved?.customer || { ...EMPTY_CUSTOMER });
  const [discount, setDiscount] = useState(saved?.discount || { mode: "flat", value: "" });
  const [note, setNote] = useState(saved?.note || "");
  const [coupon, setCoupon] = useState(saved?.coupon || null);
  const [showPayment, setShowPayment] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const clientRefRef = useRef(saved?.clientRef || newRef());
 
  const [variantPicker, setVariantPicker] = useState(null);
  const [lastAdded, setLastAdded] = useState(null);
 
  // refresh par sale na jaaye
  useEffect(() => {
    sessionStorage.setItem(CART_KEY, JSON.stringify({ cart, customer, discount, note, coupon, clientRef: clientRefRef.current }));
  }, [cart, customer, discount, note, coupon]);
 
  useEffect(() => {
    categoryAPI
      .getListforDropdown()
      .then((res) => res.code === 200 && setCategories(res.data || []))
      .catch(() => {});
  }, []);
 
  const fetchProducts = useCallback(
    async (pageNo = 1) => {
      setLoading(true);
      try {
        const res = await PosAPI.products({ page: pageNo, limit: 24, categoryId: activeCat || undefined });
        if (res.code === 200) {
          const list = res.data.products || [];
          setProducts((prev) => (pageNo === 1 ? list : [...prev, ...list]));
          setHasNext(!!res.data.pagination?.hasNext);
          setPage(pageNo);
        }
      } catch (err) {
        showError("Failed to load products");
      } finally {
        setLoading(false);
      }
    },
    [activeCat],
  );
 
  useEffect(() => {
    fetchProducts(1);
  }, [fetchProducts]);
 
  /* ---------------- CART ACTIONS ---------------- */
  const addItem = useCallback((row) => {
    if ((row.stock || 0) <= 0) {
      showError(`${row.title}${row.variantName ? ` (${row.variantName})` : ""} is out of stock`);
      return;
    }
    setCart((prev) => {
      const found = prev.find((c) => c.variantId === row.variantId);
      if (found) {
        if (found.qty + 1 > row.stock) {
          showError(`Only ${row.stock} in stock`);
          return prev;
        }
        return prev.map((c) => (c.variantId === row.variantId ? { ...c, qty: c.qty + 1, stock: row.stock } : c));
      }
      return [
        ...prev,
        {
          variantId: row.variantId,
          productId: row.productId,
          title: row.title,
          variantName: row.variantName,
          sku: row.sku,
          unitPrice: row.price, // editable (bargaining)
          listPrice: row.price, // system price
          costPrice: row.costPrice || 0,
          mrp: row.mrp,
          gstRate: row.gst,
          stock: row.stock,
          thumbnail: row.thumbnail,
          qty: 1,
        },
      ];
    });
    setLastAdded(row.variantId);
  }, []);
 
  // naya add hua item highlight + cart me scroll
  useEffect(() => {
    if (!lastAdded) return;
    document.getElementById(`cart-${lastAdded}`)?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    const t = setTimeout(() => setLastAdded(null), 900);
    return () => clearTimeout(t);
  }, [lastAdded, cart]);
 
  const onProductClick = (product) => {
    if (product.variants.length === 1) addItem(gridToRow(product, product.variants[0]));
    else setVariantPicker(product);
  };
 
  const setQty = (variantId, qty) => {
    setCart((prev) =>
      prev
        .map((c) => {
          if (c.variantId !== variantId) return c;
          const q = Math.max(0, parseInt(qty, 10) || 0);
          if (q > c.stock) {
            showError(`Only ${c.stock} in stock`);
            return { ...c, qty: c.stock };
          }
          return { ...c, qty: q };
        })
        .filter((c) => c.qty > 0),
    );
  };
 
  const setPrice = (variantId, price) =>
    setCart((prev) => prev.map((c) => (c.variantId === variantId ? { ...c, unitPrice: price } : c)));
 
  const removeItem = (variantId) => setCart((prev) => prev.filter((c) => c.variantId !== variantId));
 
  const resetSale = () => {
    setCart([]);
    setCustomer({ ...EMPTY_CUSTOMER });
    setDiscount({ mode: "flat", value: "" });
    setCoupon(null);
    setNote("");
    clientRefRef.current = newRef();
    sessionStorage.removeItem(CART_KEY);
  };
 
  /* ---------------- BILL ---------------- */
  const bill = useMemo(() => computeBill(cart, discount, coupon), [cart, discount, coupon]);
 
  const openPayment = () => {
    if (!cart.length) return showError("Cart is empty");
    if (coupon && !bill.coupon?.eligible) {
      return showError(`Coupon ${coupon.code} needs a minimum bill of ${inr(coupon.minimumCartValue)}. Remove it or add more items.`);
    }
    const digits = (customer.phone || "").replace(/\D/g, "");
    if (!customer.id && digits && digits.length < 10) return showError("Enter a valid 10-digit mobile number");
    setShowPayment(true);
  };
 
  const generateInvoice = async (payment) => {
    setSubmitting(true);
    try {
      const res = await PosAPI.createSale({
        items: cart.map((c) => ({
          variantId: c.variantId,
          qty: c.qty,
          ...(Number(c.unitPrice) !== Number(c.listPrice) ? { price: Number(c.unitPrice) } : {}),
        })),
        couponCode: coupon?.code || "",
        customer: {
          id: customer.id || null,
          name: customer.name,
          phone: customer.phone,
          email: customer.email,
          gstin: customer.gstin,
          save: customer.save,
        },
        discount: { mode: discount.mode, value: Number(discount.value) || 0 },
        payment,
        note,
        clientRef: clientRefRef.current,
      });
 
      if (res.code === 200 || res.code === 201) {
        showSuccess(res.message || "Invoice generated");
        const id = res.data._id;
        setShowPayment(false);
        resetSale();
        navigate(`/pos/offline-sales/${id}`, { state: { autoPrint: true } });
      } else {
        showError(res.message || "Could not generate invoice");
      }
    } catch (err) {
      showError(errMsg(err, "Could not generate invoice"));
      fetchProducts(1); // stock refresh
    } finally {
      setSubmitting(false);
    }
  };
 
  return (
    <div className="content pos-page">
      <div className="row g-3">
        {/* ================= LEFT: PRODUCTS ================= */}
        <div className="col-xl-8 col-lg-7">
          <div className="d-flex flex-wrap gap-2 align-items-start mb-3">
            <div className="flex-grow-1" style={{ minWidth: 280 }}>
              <PosProductSearch onSelect={addItem} onPickVariant={setVariantPicker} />
              <small className="text-muted">
                <i className="ti ti-scan me-1" />
                Scanner ready: scan a barcode or QR anytime to add it to the cart.
              </small>
            </div>
            <button className="btn btn-outline-secondary" onClick={() => navigate("/pos/offline-sales")}>
              <i className="ti ti-receipt me-1" /> Offline Sales
            </button>
          </div>
 
          <div className="pos-cats mb-3">
            <button className={`pos-cat ${!activeCat ? "active" : ""}`} onClick={() => setActiveCat("")}>
              All
            </button>
            {categories.map((c) => (
              <button key={c._id} className={`pos-cat ${activeCat === c._id ? "active" : ""}`} onClick={() => setActiveCat(c._id)}>
                {c.name}
              </button>
            ))}
          </div>
 
          <div className="pos-grid">
            {products.map((p) => {
              const out = p.totalStock <= 0;
              const inCart = cart.some((c) => c.productId === p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  className={`pos-card ${inCart ? "in-cart" : ""}`}
                  disabled={out}
                  onClick={() => onProductClick(p)}
                >
                  {inCart && <i className="ti ti-circle-check-filled pos-check" />}
                  <div className="pos-thumb">
                    {p.thumbnail ? <img src={p.thumbnail} alt={p.title} loading="lazy" /> : <i className="ti ti-photo" />}
                  </div>
                  <small className="text-muted d-block text-truncate">{p.category}</small>
                  <div className="fw-semibold text-truncate" title={p.title}>
                    {p.title}
                  </div>
                  <div className="d-flex justify-content-between align-items-center mt-1">
                    <span className="fw-bold text-dark">{p.minPrice === p.maxPrice ? inr(p.minPrice) : `${inr(p.minPrice)}+`}</span>
                    <span className={`badge ${out ? "bg-danger" : p.totalStock <= 5 ? "bg-warning" : "bg-light text-dark"}`}>
                      {out ? "Out of stock" : `${p.totalStock} ${p.unit || ""}`}
                    </span>
                  </div>
                  {p.variants.length > 1 && <small className="text-primary">{p.variants.length} variants</small>}
                </button>
              );
            })}
          </div>
 
          {!loading && !products.length && <div className="text-center text-muted py-5">No products found in this category.</div>}
          {loading && <div className="text-center py-4">Loading...</div>}
          {hasNext && !loading && (
            <div className="text-center my-3">
              <button className="btn btn-outline-primary" onClick={() => fetchProducts(page + 1)}>
                Load more
              </button>
            </div>
          )}
        </div>
 
        {/* ================= RIGHT: ORDER ================= */}
        <div className="col-xl-4 col-lg-5">
          <div className="card pos-order">
            <div className="card-header d-flex justify-content-between align-items-center">
              <h5 className="mb-0">Current Sale</h5>
              {(cart.length > 0 || customer.id || customer.phone) && (
                <button className="btn btn-sm btn-outline-danger" onClick={resetSale}>
                  Clear all
                </button>
              )}
            </div>
 
            <div className="card-body">
              <PosCustomerSearch value={customer} onChange={setCustomer} />
 
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">Items</h6>
                <span className="badge bg-light text-dark">Qty: {bill.totalQty}</span>
              </div>
 
              <div className="pos-cart">
                {!cart.length && (
                  <div className="text-center text-muted py-4 small">
                    <i className="ti ti-shopping-cart d-block fs-24 mb-1" />
                    Your cart is empty. Search, scan or select a product to add it.
                  </div>
                )}
                {cart.map((c) => (
                  <div key={c.variantId} id={`cart-${c.variantId}`} className={`pos-cart-row ${lastAdded === c.variantId ? "flash" : ""}`}>
                    <div className="flex-grow-1 overflow-hidden">
                      <div className="fw-medium text-truncate" title={c.title}>
                        {c.title}
                      </div>
                      <CartPrice item={c} onChange={(p) => setPrice(c.variantId, p)} />
                    </div>
                    <div className="pos-qty">
                      <button onClick={() => setQty(c.variantId, c.qty - 1)}>−</button>
                      <input value={c.qty} onChange={(e) => setQty(c.variantId, e.target.value)} />
                      <button onClick={() => setQty(c.variantId, c.qty + 1)}>+</button>
                    </div>
                    <div className="text-end" style={{ minWidth: 80 }}>
                      <div className="fw-semibold">{inr(c.unitPrice * c.qty)}</div>
                      <button className="btn btn-link btn-sm p-0 text-danger" onClick={() => removeItem(c.variantId)} title="Remove">
                        <i className="ti ti-trash" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
 
              <div className="mt-3">
                <PosCouponBox
                  applied={coupon}
                  billCoupon={bill.coupon}
                  subtotal={bill.totals.subtotal}
                  customerId={customer.id}
                  onApply={setCoupon}
                  onRemove={() => setCoupon(null)}
                />
              </div>
 
              <div className="row g-2 mt-2 align-items-center">
                <div className="col-5">
                  <label className="form-label small mb-0">Extra discount</label>
                </div>
                <div className="col-3">
                  <select className="form-select form-select-sm" value={discount.mode} onChange={(e) => setDiscount({ ...discount, mode: e.target.value })}>
                    <option value="flat">₹</option>
                    <option value="percent">%</option>
                  </select>
                </div>
                <div className="col-4">
                  <input
                    className="form-control form-control-sm"
                    placeholder="0"
                    value={discount.value}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === "" || /^\d+(\.\d{0,2})?$/.test(val)) setDiscount({ ...discount, value: val });
                    }}
                  />
                </div>
              </div>
 
              <textarea
                className="form-control form-control-sm mt-2"
                rows={1}
                placeholder="Note (optional)"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
 
              <div className="pos-totals mt-3">
                {bill.totals.itemDiscount > 0 && (
                  <>
                    <div className="text-muted small">
                      <span>Items at listed price</span>
                      <span>{inr(bill.totals.subtotal + bill.totals.itemDiscount)}</span>
                    </div>
                    <div className="text-success small">
                      <span>Price adjustments</span>
                      <span>− {inr(bill.totals.itemDiscount)}</span>
                    </div>
                  </>
                )}
                <div><span>Sub Total</span><span>{inr(bill.totals.subtotal)}</span></div>
                {bill.totals.couponDiscount > 0 && (
                  <div className="text-success"><span>Coupon ({coupon?.code})</span><span>− {inr(bill.totals.couponDiscount)}</span></div>
                )}
                {bill.totals.manualDiscount > 0 && (
                  <div className="text-success">
                    <span>Extra discount{discount.mode === "percent" ? ` (${discount.value}%)` : ""}</span>
                    <span>− {inr(bill.totals.manualDiscount)}</span>
                  </div>
                )}
                <div className="text-muted small"><span>Taxable</span><span>{inr(bill.totals.taxable)}</span></div>
                <div className="text-muted small"><span>CGST</span><span>{inr(bill.totals.cgst)}</span></div>
                <div className="text-muted small"><span>SGST</span><span>{inr(bill.totals.sgst)}</span></div>
                {bill.totals.roundOff !== 0 && (
                  <div className="text-muted small"><span>Round off</span><span>{inr(bill.totals.roundOff)}</span></div>
                )}
                <div className="grand"><span>Grand Total</span><span>{inr(bill.totals.grandTotal)}</span></div>
                <small className="text-muted">All prices are inclusive of GST.</small>
              </div>
 
              <button className="btn btn-primary w-100 mt-3 py-2" disabled={!cart.length || submitting} onClick={openPayment}>
                <i className="ti ti-cash-register me-1" /> Proceed to Payment
              </button>
            </div>
          </div>
        </div>
      </div>
 
      {/* ================= VARIANT PICKER ================= */}
      {variantPicker && (
        <>
          <div className="modal fade show d-block" tabIndex={-1} onMouseDown={(e) => e.target === e.currentTarget && setVariantPicker(null)}>
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Select variant · {variantPicker.title}</h5>
                  <button className="btn-close" onClick={() => setVariantPicker(null)} />
                </div>
                <div className="modal-body">
                  <div className="list-group">
                    {variantPicker.variants.map((v) => (
                      <button
                        key={v.id}
                        className="list-group-item list-group-item-action d-flex justify-content-between align-items-center"
                        disabled={v.stock <= 0}
                        onClick={() => {
                          addItem(gridToRow(variantPicker, v));
                          setVariantPicker(null);
                        }}
                      >
                        <span>
                          <b>{v.name || v.sku}</b>
                          <small className="d-block text-muted">{v.sku}</small>
                        </span>
                        <span className="text-end">
                          <b>{inr(v.price)}</b>
                          <small className={`d-block ${v.stock <= 0 ? "text-danger" : "text-muted"}`}>
                            {v.stock <= 0 ? "Out of stock" : `${v.stock} in stock`}
                          </small>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show" />
        </>
      )}
 
      {/* ================= PAYMENT ================= */}
      {showPayment && (
        <PaymentModal
          total={bill.totals.grandTotal}
          title="Payment"
          submitting={submitting}
          onClose={() => setShowPayment(false)}
          onConfirm={generateInvoice}
        />
      )}
    </div>
  );
}

export default List;